/**
 * The `/api/render` transport: POST the render payload and turn anything other
 * than a 2xx into a thrown `RenderDispatchError`. Kept apart from
 * `VideoPublisher` (which only assembles the payload) so the failure handling
 * is pure and testable without a Story/Video/analytics harness.
 */

import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

const RENDER_URL = `${process.env.REACT_APP_API_URL!}/api/render`;

const logger = LogManager.getLogger('service:renderDispatch');

/**
 * The dispatch error is persisted to the PublishRecord's `errorMessage` and
 * rendered verbatim in the downloads / publish-progress UI, so a server error
 * body (which can be a full HTML page) is collapsed and truncated to this.
 */
const MAX_ERROR_SNIPPET_CHARS = 300;

/**
 * The render request never reached (or was rejected by) `/api/render`, so no
 * render was started. Callers must treat this as a failed dispatch — see
 * `PublishFanoutService.buildAndDispatch`, which marks the cell's record failed.
 */
export class RenderDispatchError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly payloadBytes: number,
  ) {
    super(message);
    this.name = 'RenderDispatchError';
  }
}

/** Identifies the failed dispatch in the logs. */
export type RenderDispatchContext = {
  videoId?: string;
  publishRecordId?: string;
  groupId?: string;
};

/**
 * POST the render payload. Resolves once the render has been accepted; throws
 * `RenderDispatchError` if it was not, so the caller can fail its record.
 */
export async function postRenderRequest(
  payload: unknown,
  context: RenderDispatchContext,
): Promise<void> {
  const body = JSON.stringify(payload);

  let response: Response;
  try {
    response = await fetch(RENDER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
  } catch (err) {
    throw failed(null, body, extractErrorInfo(err).errorMessage, context);
  }

  // fetch only rejects on network-level failures: an HTTP error resolves with
  // `ok === false`, so without this the dispatch silently "succeeds" and the
  // publish record is never marked failed (e.g. a 413 when a long story's
  // source payload exceeds the request-body limit).
  if (!response.ok) {
    throw failed(
      response.status,
      body,
      await readResponseSnippet(response),
      context,
    );
  }
}

/** Log the failed dispatch and build the error persisted to the record. */
function failed(
  status: number | null,
  body: string,
  detail: string,
  context: RenderDispatchContext,
): RenderDispatchError {
  const payloadBytes = new TextEncoder().encode(body).length;
  const message = buildMessage(status, payloadBytes, detail);

  logger.error('Render dispatch failed', {
    status,
    payloadBytes,
    detail,
    ...context,
  });

  return new RenderDispatchError(message, status, payloadBytes);
}

function buildMessage(
  status: number | null,
  payloadBytes: number,
  detail: string,
): string {
  // A 413 is the server itself reporting the body was too big, so the size is
  // the cause and worth naming.
  if (status === 413) {
    return `Render payload too large (${formatPayloadSize(payloadBytes)}) — the render service rejected the request.`;
  }

  // No status means fetch rejected: offline, DNS, reset connection, a blocked
  // request, or a rejection at the platform edge that arrived without CORS
  // headers. The size is worth reporting as evidence, but it is NOT proof of a
  // size rejection — a dropped connection mid-publish looks identical, and a
  // long story's payload is always large.
  if (status === null) {
    return `Render request failed: ${detail} (payload ${formatPayloadSize(payloadBytes)})`;
  }

  return `Render request failed (${status}): ${detail}`;
}

/** KB below a megabyte — `0.0 MB` tells the reader nothing. */
function formatPayloadSize(bytes: number): string {
  return bytes < 1_000_000
    ? `${Math.round(bytes / 1000)} KB`
    : `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * Best-effort one-line summary of an error response body — JSON `message`,
 * otherwise the collapsed, truncated raw body (Next/Vercel error pages are
 * multi-line HTML).
 */
async function readResponseSnippet(response: Response): Promise<string> {
  const fallback = response.statusText || 'no response body';
  let raw: string;
  try {
    raw = await response.text();
  } catch {
    return fallback;
  }
  if (!raw) return fallback;

  try {
    const parsed = JSON.parse(raw) as { message?: unknown };
    if (typeof parsed.message === 'string') {
      // Server messages can carry newlines too (a wrapped cause, a stack), so
      // they go through the same normalization as a raw body.
      const message = oneLine(parsed.message);
      if (message) return message;
    }
  } catch {
    // Not JSON — fall through to the raw body.
  }

  return oneLine(raw);
}

/** Single line, length-capped: this lands in a Dato field and in the UI. */
function oneLine(raw: string): string {
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  return collapsed.length > MAX_ERROR_SNIPPET_CHARS
    ? `${collapsed.slice(0, MAX_ERROR_SNIPPET_CHARS)}…`
    : collapsed;
}
